import {DOMAIN_TOKENS} from './di.domain.tokens';

import {Container} from 'brandi';

import {Environment} from '@/envs';
import {DailyRewardsService} from '@/services/DailyRewardsService/DailyRewardsService';
import {DevPanelService} from '@/services/DevPanelService/DevPanelService';
import {TooltipsService} from '@/services/TooltipsService/TooltipsService';
import {WindowVisibilityService} from '@/services/WindowVisibilityService/WindowVisibilityService';
import {GameOperationsService} from '@/services/shared/GameOperationsService/GameOperationsService';
import {GameStatisticsDevPanel} from '@/services/shared/GameStatisticsService/GameStatisticsDevPanel';
import {GameStatisticsService} from '@/services/shared/GameStatisticsService/GameStatisticsService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {GiftsDevPanel} from '@/services/shared/GiftsService/GiftsDevPanel';
import {GiftsService} from '@/services/shared/GiftsService/GiftsService';
import {ImprovementsService} from '@/services/shared/ImprovementsService/ImprovementsService';
import {LeaderBoardDevPanel} from '@/services/shared/LeaderBoardService/LeaderBoardDevPanel';
import {LeaderBoardService} from '@/services/shared/LeaderBoardService/LeaderBoardService';
import {OnboardingService} from '@/services/shared/OnboardingService/OnboardingService';
import {SettingsService} from '@/services/shared/SettingsService/SettingsService';
import {ShareService} from '@/services/shared/ShareService/ShareService';
import {WinnersService} from '@/services/shared/WinnersService/WinnersService';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {DailyGameStore} from '@/stores/DailyGameStore/DailyGameStore';
import {DailyRewardsStore} from '@/stores/DailyRewardsStore/DailyRewardsStore';
import {GameStatisticsStore} from '@/stores/GameStatisticsStore/GameStatisticsStore';
import {GameStatusStore} from '@/stores/GameStatusStore/GameStatusStore';
import {GiftsStore} from '@/stores/GiftsStore/GiftsStore';
import {ImprovementsStore} from '@/stores/ImprovementsStore/ImprovementsStore';
import {LeaderBoardStore} from '@/stores/LeaderBoardStore/LeaderBoardStore';
import {NavigationClickerModalStore} from '@/stores/NavigationClickerModalStore/NavigationClickerModalStore';
import {OnboardingStore} from '@/stores/OnboardingStore/OnboardingStore';
import {SettingsStore} from '@/stores/SettingsStore/SettingsStore';
import {ShareStore} from '@/stores/ShareStore/ShareStore';
import {TasksStore} from '@/stores/TasksStore/TasksStore';
import {TooltipsStore} from '@/stores/TooltipsStore/TooltipsStore';
import {UserStore} from '@/stores/UserStore/UserStore';
import {WinnersStore} from '@/stores/WinnersStore/WinnersStore';
import {MainPageAnimation} from '@/ui/components/MainPageAnimation/MainPageAnimation';
import {ConfettiAnimation} from '@/ui/kit/ConfettiAnimation/ConfettiAnimation';

export function initDomainDeps(container: Container): void {
  if (process.env.APP_PROJECT_API_MOCK_ENABLED === 'true') {
    container
      .bind(DOMAIN_TOKENS.projectApi)
      .toInstance(require('@/services/ProjectApi/ProjectApiMock').ProjectApiMock)
      .inSingletonScope();
  } else {
    container
      .bind(DOMAIN_TOKENS.projectApi)
      .toInstance(require('@/services/ProjectApi/ProjectApi').ProjectApi)
      .inSingletonScope();
  }

  container.bind(DOMAIN_TOKENS.userStore).toInstance(UserStore).inSingletonScope();

  container.bind(DOMAIN_TOKENS.tasksStore).toInstance(TasksStore).inSingletonScope();

  container.bind(DOMAIN_TOKENS.leaderBoardStore).toInstance(LeaderBoardStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.leaderBoardService).toInstance(LeaderBoardService).inSingletonScope();
  container.bind(DOMAIN_TOKENS.leaderBoardDevPanel).toInstance(LeaderBoardDevPanel).inSingletonScope();

  container.bind(DOMAIN_TOKENS.dailyGameStore).toInstance(DailyGameStore).inSingletonScope();

  container.bind(DOMAIN_TOKENS.navigationClickerModalStore).toInstance(NavigationClickerModalStore).inSingletonScope();

  container.bind(DOMAIN_TOKENS.clickerStateStore).toInstance(ClickerStateStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.gameOperationsService).toInstance(GameOperationsService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.gameStatusStore).toInstance(GameStatusStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.gameStatusService).toInstance(GameStatusService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.improvementsStore).toInstance(ImprovementsStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.improvementsService).toInstance(ImprovementsService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.dailyRewardsStore).toInstance(DailyRewardsStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.dailyRewardsService).toInstance(DailyRewardsService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.shareStore).toInstance(ShareStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.shareService).toInstance(ShareService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.giftsStore).toInstance(GiftsStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.giftsService).toInstance(GiftsService).inSingletonScope();
  container.bind(DOMAIN_TOKENS.giftsDevPanel).toInstance(GiftsDevPanel).inSingletonScope();

  container.bind(DOMAIN_TOKENS.windowVisibilityService).toInstance(WindowVisibilityService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.onboardingStore).toInstance(OnboardingStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.onboardingService).toInstance(OnboardingService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.settingsStore).toInstance(SettingsStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.settingsService).toInstance(SettingsService).inSingletonScope();

  const isDevPanelEnabled = process.env.APP_ENVIRONMENT !== Environment.Prod;
  const DevPanelConstructor = isDevPanelEnabled
    ? require('@/services/DevPanel/DevPanel').DevPanel
    : require('@/services/DevPanel/DevPanel.Fake').DevPanelFake;
  container.bind(DOMAIN_TOKENS.devPanel).toInstance(DevPanelConstructor).inSingletonScope();
  container.bind(DOMAIN_TOKENS.devPanelService).toInstance(DevPanelService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.mainPageAnimation).toInstance(MainPageAnimation).inSingletonScope();

  container.bind(DOMAIN_TOKENS.tooltipsStore).toInstance(TooltipsStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.tooltipsService).toInstance(TooltipsService).inSingletonScope();

  container.bind(DOMAIN_TOKENS.gameStatisticsStore).toInstance(GameStatisticsStore).inSingletonScope();
  container.bind(DOMAIN_TOKENS.gameStatisticsService).toInstance(GameStatisticsService).inSingletonScope();
  container.bind(DOMAIN_TOKENS.gameStatisticsDevPanel).toInstance(GameStatisticsDevPanel).inSingletonScope();

  container.bind(DOMAIN_TOKENS.confettiAnimation).toInstance(ConfettiAnimation).inTransientScope();

  container.bind(DOMAIN_TOKENS.winnersService).toInstance(WinnersService).inSingletonScope();
  container.bind(DOMAIN_TOKENS.winnersStore).toInstance(WinnersStore).inSingletonScope();
}
